import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingBag, Menu, X } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { Logo } from "./Logo";

export const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/shop", label: "Shop All", id: "shop" },
  { to: "/shop?new=1", label: "New Arrivals", id: "new" },
  { to: "/contact", label: "Contact", id: "contact" },
];

const SearchBar = ({ onClose }) => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const submit = (e) => { e.preventDefault(); navigate(`/shop?q=${encodeURIComponent(q)}`); onClose(); };
  return (
    <form onSubmit={submit} className="border-t border-[var(--mm-line)] bg-[var(--mm-cream)] px-5 sm:px-10 py-5 flex items-center gap-4 rise">
      <Search size={16} className="text-[var(--mm-red)]" />
      <input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the collection" className="flex-1 bg-transparent outline-none font-serif-mm text-2xl" />
      <button type="button" data-testid="search-close-button" onClick={onClose} aria-label="Close search"><X size={18} /></button>
    </form>
  );
};

export const Header = () => {
  const { count, setBagOpen, settings } = useShop();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const loc = useLocation();
  useEffect(() => { setMenu(false); setSearch(false); }, [loc.pathname, loc.search]);
  const isActive = (to) => (to === "/" ? loc.pathname === "/" : `${loc.pathname}${loc.search}` === to || (to === "/shop" && loc.pathname === "/shop" && !loc.search.includes("new")));

  return (
    <header data-testid="site-header" className="sticky top-0 z-40">
      {settings?.announcement && (
        <div data-testid="announcement-bar" className="bg-[var(--mm-red)] text-[var(--mm-cream)] text-center eyebrow py-2.5 px-4">{settings.announcement}</div>
      )}
      <div className="bg-[var(--mm-cream)]/90 backdrop-blur-md border-b border-[var(--mm-line)]">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center px-5 sm:px-10 h-20 lg:h-24">
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink key={n.id} to={n.to} data-testid={`nav-${n.id}-link`} className={() => `nav-link ${isActive(n.to) ? "active text-[var(--mm-red)]" : ""}`}>{n.label}</NavLink>
            ))}
          </nav>
          <button className="lg:hidden justify-self-start" data-testid="mobile-menu-toggle" onClick={() => setMenu(!menu)} aria-label="Menu">
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
          <Link to="/" data-testid="nav-logo" className="justify-self-center"><Logo className="h-11 lg:h-14" testId="header-logo-img" /></Link>
          <div className="justify-self-end flex items-center gap-5">
            <button data-testid="search-toggle-button" onClick={() => setSearch(!search)} aria-label="Search" className="hover:text-[var(--mm-red)] transition-colors"><Search size={19} /></button>
            <button data-testid="bag-drawer-toggle" onClick={() => setBagOpen(true)} aria-label="Bag" className="relative flex items-center gap-2 hover:text-[var(--mm-red)] transition-colors">
              <ShoppingBag size={20} />
              <span data-testid="bag-count" className="nav-link">({count})</span>
            </button>
          </div>
        </div>
        {search && <SearchBar onClose={() => setSearch(false)} />}
        {menu && (
          <nav data-testid="mobile-menu" className="lg:hidden border-t border-[var(--mm-line)] px-5 py-8 flex flex-col gap-6 bg-[var(--mm-cream)]">
            {NAV.map((n, i) => (
              <Link key={n.id} to={n.to} data-testid={`mobile-nav-${n.id}-link`} className={`font-serif-mm text-4xl rise d${Math.min(i, 4)} ${isActive(n.to) ? "text-[var(--mm-red)] italic" : ""}`}>{n.label}</Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};
